import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CreditCard, ExternalLink, Tag, X } from 'lucide-react';
import SearchableDropdown from '../SearchableDropdown';
import { api } from '../../services/apiService';
import { toastService } from '../../services/toastService';
import PlanAssignmentBuilder from './PlanAssignmentBuilder';
import { Note } from './financeUi';
import { money, locationMonthlyCents, type LocationRow } from './financeFormat';
import type { PricePlan } from '../../types';

import { RubyGemMark } from '../RubyGemMark';
/**
 * FINANCE → LOCATIONS → CHANGE PRICE
 *
 *   > *"for each location subscription when we click change price it will show
 *   > you all the packages it has, what are the current prices given for this
 *   > client. we can add more discount or remove discount and add and enable
 *   > additional packages, disable packages and adjust final subscription
 *   > price. save it, publish it, will affect immediately and admin and client
 *   > both will get notification with what changes email clearly"*
 *
 * ---------------------------------------------------------------------------
 * THIS IS A SHELL, NOT A SECOND EDITOR
 * ---------------------------------------------------------------------------
 *
 * Everything about packages, discounts and the published price is
 * `PlanAssignmentBuilder` in its `focusLocationId` mode — the SAME component
 * the Plan panel uses. A second copy of that screen is the worst outcome
 * available here: two editors of the same money, agreeing right up until the
 * day one of them is changed. So this file owns only what the builder cannot:
 * the modal, and the honest presentation of the line the builder does not touch.
 *
 * ---------------------------------------------------------------------------
 * TWO LINES, BOTH SHOWN
 * ---------------------------------------------------------------------------
 *
 * A location's bill is made of two independent things, and conflating them is
 * how an operator ends up believing they changed a price they did not:
 *
 *  1. **The base plan line** — owned by `location-billing-service` (free quota,
 *     per-location price, approval). It already works and it STAYS. Its price
 *     picker is kept here, unchanged, so nothing this screen replaces is lost.
 *  2. **The managed software line** — the packages, at the net our own
 *     arithmetic computed (§13/B1). `subscriptionSync` ADDS it beside the base
 *     line and never touches a line it did not stamp.
 *
 * The builder's composed figure is therefore NOT this store's whole bill, and
 * it is never captioned as if it were. Both numbers are on screen, separately,
 * with the total stated as the sum of two things rather than implied.
 */

interface Props {
  tenantId: string;
  /** The row, looked up fresh from the panel's list on every render — see LocationsPanel. */
  row: LocationRow;
  /** Catalog SKUs, already loaded by the Finance tab. */
  skus: any[];
  /** Every location on this tenant — the builder names the ids its composition returns. */
  locations: LocationRow[];
  /** Stripe prices, for the base plan line. */
  prices: PricePlan[];
  onClose: () => void;
  /** Reload the tenant's locations after anything here changes money. */
  onReloadLocations: () => Promise<void> | void;
  notify: (message: string) => void;
}

const ChangePriceEditor: React.FC<Props> = ({
  tenantId, row, skus, locations, prices, onClose, onReloadLocations, notify,
}) => {
  const [basePriceId, setBasePriceId] = useState<string>(row.priceId || '');
  /**
   * `useState` seeds ONCE. Publishing reloads the tenant's locations and this
   * editor re-renders against a fresh row (that is deliberate — see
   * LocationsPanel), so without this the base-price dropdown would keep showing
   * the selection from before the reload while everything around it refreshed.
   *
   * It follows the SERVER's value, and only when the server's value actually
   * changes — a plain `useEffect` on `row.priceId` would also fire on every
   * unrelated reload and throw away a selection the operator was midway through
   * making.
   */
  const serverPriceId = useRef(row.priceId || '');
  useEffect(() => {
    const incoming = row.priceId || '';
    if (incoming === serverPriceId.current) return;
    serverPriceId.current = incoming;
    setBasePriceId(incoming);
  }, [row.priceId]);
  const [applyingBase, setApplyingBase] = useState(false);
  /**
   * The base-price change is a SEPARATE confirmation inside this modal, with its
   * own state. It deliberately does not share the builder's — an earlier task
   * shipped a reused handler whose "done" step cleared the very panel that
   * rendered it, and the same shape of bug here would close this editor out from
   * under an operator mid-edit.
   */
  const [confirmBase, setConfirmBase] = useState(false);

  const priceOptions = useMemo(
    () => prices.map((p) => ({
      value: p.id,
      label: p.nickname || p.productName || p.id,
      subtitle: `${money(p.unitAmount)}/${p.interval}`,
    })),
    [prices],
  );

  const baseCents = locationMonthlyCents(row);

  /**
   * A location that has not been approved yet has no `locationId`, and the
   * composition is keyed by `locationId` — so there is nothing for the builder
   * to scope to. Said plainly rather than rendering an editor whose switches
   * would silently do nothing.
   */
  const unapproved = !row.locationId;

  const applyBasePrice = async () => {
    if (!basePriceId) return;
    setApplyingBase(true);
    try {
      await api.locationBilling.changePriceRequest(row.requestId, { tenantId, priceId: basePriceId });
      notify(`${row.name || 'Location'} moved to a new base plan price.`);
      setConfirmBase(false);
      await onReloadLocations();
    } catch (err: any) {
      toastService.error(err?.message || 'The base plan price could not be changed.');
    } finally {
      setApplyingBase(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-center p-3 sm:p-6 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-stone-100 text-stone-800 rounded-2xl border border-stone-200 shadow-xl max-w-4xl w-full my-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Dark header, per the platform's page-header standard. */}
        <div className="bg-stone-900 text-white rounded-t-2xl px-5 py-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-bold truncate">Change price — {row.name || row.requestId}</h2>
            <p className="text-xs text-stone-300 mt-0.5">
              What this client pays, package by package. Publishing takes effect immediately, with proration,
              and emails the client and our admins exactly what changed.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-stone-300 hover:text-white p-2 -m-1 min-h-[44px] min-w-[44px] inline-flex items-center justify-center"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-4 sm:p-5 space-y-4">
          {/* 1 — the base plan line, which this editor does NOT compose ------ */}
          <div className="bg-white text-stone-800 rounded-2xl border border-stone-200/70 p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-[14rem]">
                <p className="text-[10px] uppercase tracking-wider text-stone-500">Base plan line</p>
                <p className="text-xl font-bold tabular-nums text-stone-900">
                  {row.isFree ? 'Free' : money(baseCents)}
                  {!row.isFree && <span className="text-sm font-medium text-stone-500">/mo</span>}
                </p>
                <p className="text-[11px] text-stone-500 mt-0.5">
                  {row.isFree
                    ? 'This store is comped — it has no base subscription.'
                    : <>{row.priceName || row.priceId || 'no price on file'} · billed by the per-location subscription</>}
                </p>
              </div>
              <div className="flex-1 min-w-[16rem]">
                <label className="block text-[10px] uppercase tracking-wider text-stone-500 mb-1">
                  Move the base plan to a different price
                </label>
                <div className="flex items-end gap-2 flex-wrap">
                  <div className="flex-1 min-w-[12rem]">
                    <SearchableDropdown
                      options={priceOptions}
                      value={basePriceId || null}
                      onChange={(v) => setBasePriceId(v || '')}
                      placeholder="Select a price…"
                      searchPlaceholder="Search prices…"
                      variant="light"
                    />
                  </div>
                  <button
                    onClick={() => setConfirmBase(true)}
                    disabled={!basePriceId || basePriceId === row.priceId || applyingBase}
                    className="px-4 py-3 rounded-xl border border-stone-300 text-stone-700 text-sm font-semibold hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed min-h-[44px] inline-flex items-center gap-1.5"
                  >
                    {applyingBase ? <RubyGemMark size={14} drawing color="currentColor" /> : <Tag size={14} />} Apply
                  </button>
                </div>
              </div>
            </div>
            <p className="text-[11px] text-stone-500 mt-2.5 leading-relaxed">
              This is the per-location subscription, and it is a <strong>separate line</strong> from the packages
              below — changing one never changes the other. Stripe prices are immutable, so this swaps the price
              rather than editing it.
            </p>
          </div>

          {/* 2 — the packages, composed by billing-service ------------------- */}
          {unapproved ? (
            <Note tone="warn">
              <strong>This location has not been approved yet</strong>, so it has no location id and nothing can be
              billed to it. Approve it from its own page first; package pricing becomes editable here after that.
            </Note>
          ) : (
            <PlanAssignmentBuilder
              tenantId={tenantId}
              skus={skus}
              locations={locations}
              /**
               * The base line, stated so the builder shows the composed amount
               * BESIDE it rather than as if it replaced it. Only this store's
               * base line — the builder is scoped to this store, so folding in
               * the tenant's other locations would caption one store's change
               * against another store's bill.
               */
              baseSubscriptionCents={baseCents}
              focusLocationId={row.locationId}
              focusLocationName={row.name || row.locationId}
              headerHint="packages · discounts · the price this client actually pays here"
              onActivated={onReloadLocations}
            />
          )}

          <Note>
            <strong>Two lines, one store.</strong> The base plan above and the packages below are billed as two
            separate lines on this location's subscription. This store's monthly is{' '}
            <strong>{money(baseCents)}</strong> for the plan <em>plus</em> whatever the packages compose to — the
            builder shows that figure and never folds the base line into it.
          </Note>

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <a
              href={`/tenant/${tenantId}/location/${row.requestId}?tab=billing`}
              className="text-xs text-stone-600 hover:text-stone-900 inline-flex items-center gap-1.5"
            >
              Open this location&rsquo;s own billing page <ExternalLink size={12} />
            </a>
            <button
              onClick={onClose}
              className="px-4 py-3 rounded-xl bg-stone-200 text-stone-700 text-sm font-semibold hover:bg-stone-300 min-h-[44px]"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Base-price confirmation. Its own dialog, its own state — nothing here
          can close the editor it sits inside. */}
      {confirmBase && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
          onClick={(e) => { e.stopPropagation(); setConfirmBase(false); }}
        >
          <div
            className="bg-white text-stone-800 rounded-2xl border border-stone-200 shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-stone-900 mb-3">Move the base plan price?</h3>
            <div className="space-y-3 text-sm text-stone-700">
              <p className="flex items-start gap-2">
                <CreditCard size={16} className="text-stone-500 mt-0.5 shrink-0" />
                <span>
                  {row.isFree
                    ? 'This store is free today. Giving it a price moves it to payment_pending until the tenant pays the first invoice.'
                    : <>Its base plan line moves from <strong>{money(baseCents)}</strong>/mo to{' '}
                      <strong>{money(prices.find((p) => p.id === basePriceId)?.unitAmount ?? 0)}</strong>/mo.</>}
                </span>
              </p>
              <Note tone="warn">
                This does <strong>not</strong> change the packages below, and it does not send the plan-change
                emails — those belong to publishing a plan.
              </Note>
            </div>
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => setConfirmBase(false)}
                className="flex-1 px-4 py-3 bg-stone-100 text-stone-700 font-semibold rounded-xl hover:bg-stone-200 min-h-[44px]"
              >
                Cancel
              </button>
              <button
                onClick={applyBasePrice}
                disabled={applyingBase}
                className="flex-1 px-4 py-3 bg-stone-900 text-white font-bold rounded-xl hover:bg-black disabled:opacity-40 min-h-[44px]"
              >
                {applyingBase ? 'Working…' : 'Apply price'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChangePriceEditor;
